import type { LocaleCode } from '#types/locale'
import { isString } from '#core/utils/is'

/**
 * Formats a date according to the supplied locale, applying Canvas's locale-mapping table by
 * default so dates display in the expected DD/MM/YYYY (EMEA) or MM/DD/YYYY (NORA) format.
 * Returns an empty string when the input is falsy or an invalid `Date`.
 * @param date - A `Date` instance or an ISO/parseable date string.
 * @param locale - Locale to drive `toLocaleDateString`. Defaults to `'en-us'`.
 * @param long - When true, renders the month name in full (e.g. `January`) and skips the locale mapping.
 * @param applyMapping - When true (default), normalizes the locale via `getMappedDateLocale` before formatting.
 * @param timeZone - Optional IANA time zone used when converting the date for display.
 * @returns The formatted date string, or an empty string when the input is unusable.
 * @example
 * getFormattedDate(new Date('2021-01-01')) // '01/01/2021'
 * @category Utils
 */
export const getFormattedDate = (
  date: Date | string,
  locale: LocaleCode = 'en-us',
  long = false,
  applyMapping = true,
  timeZone?: string,
) => {
  if (!date) return ''

  // if date is a string, convert it to a Date object
  if (isString(date)) date = new Date(date)

  // if the date is invalid, return an empty string
  if (Number.isNaN(date.getTime())) return ''

  // Only apply mapping when long === false to display the month in the correct language.
  if (!long && applyMapping) locale = getMappedDateLocale(locale)

  return dateFormatLocale(date, locale, long, timeZone)
}
